import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Logo from '../../components/common/Logo';

const SPORTS = [
  'chess', 'cricket', 'tennis', 'football', 'badminton', 'basketball', 'swimming', 'athletics'
];

const SignupParentPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUserData } = useAuth();

  const [step, setStep] = useState<1 | 2>(1);
  const [loading, setLoading] = useState(false);
  const [parent, setParent] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
  });
  const [child, setChild] = useState({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    sport: '',
  });

  const goNext = (e: React.FormEvent) => {
    e.preventDefault();
    if (!parent.firstName || !parent.lastName || !parent.email || !parent.password) {
      toast.error('Please fill out all fields');
      return;
    }
    if (parent.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setStep(2);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!child.firstName || !child.lastName || !child.dateOfBirth || !child.sport) {
      toast.error('Please fill out the child details');
      return;
    }
    setLoading(true);
    try {
      const resp = await api.post('/auth/signup-parent', {
        parent,
        child: {
          firstName: child.firstName,
          lastName: child.lastName,
          dateOfBirth: child.dateOfBirth,
          sports: [child.sport],
        },
      });
      if (resp.data.success) {
        const { token, user } = resp.data.data;
        setUserData(user, token);
        toast.success('Parent account created!');
        navigate('/parent');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.message || 'Signup failed';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-6">
          <Link to="/signup" className="text-gray-600 hover:text-gray-800 mb-4 inline-block text-sm">
            ← Back to normal signup
          </Link>
          <Link to="/" aria-label="Sportofolo home" className="inline-block mb-4">
            <Logo variant="app-icon" className="w-16 h-16 mx-auto" />
          </Link>
          <div className="flex justify-center space-x-2 mb-5">
            <div className={`w-2 h-2 rounded-full ${step === 1 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
            <div className={`w-2 h-2 rounded-full ${step === 2 ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            {step === 1 ? 'Parent account' : 'Add your first child'}
          </h1>
          <p className="text-gray-600 mt-2 text-sm">
            {step === 1
              ? 'You manage your child’s profile. They cannot log in on their own.'
              : 'Under Indian law, players under 16 need a guardian on their account.'}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={goNext} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="First name"
                value={parent.firstName}
                onChange={(e) => setParent({ ...parent, firstName: e.target.value })}
                className="input-field"
                required
              />
              <input
                type="text"
                placeholder="Last name"
                value={parent.lastName}
                onChange={(e) => setParent({ ...parent, lastName: e.target.value })}
                className="input-field"
                required
              />
            </div>
            <input
              type="email"
              placeholder="Your email"
              value={parent.email}
              onChange={(e) => setParent({ ...parent, email: e.target.value })}
              className="input-field"
              required
            />
            <input
              type="tel"
              placeholder="Phone (optional)"
              value={parent.phone}
              onChange={(e) => setParent({ ...parent, phone: e.target.value })}
              className="input-field"
            />
            <input
              type="password"
              placeholder="Create a password (min 6 chars)"
              value={parent.password}
              onChange={(e) => setParent({ ...parent, password: e.target.value })}
              className="input-field"
              minLength={6}
              required
            />
            <button
              type="submit"
              className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
            >
              Continue
            </button>
          </form>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Child first name"
                value={child.firstName}
                onChange={(e) => setChild({ ...child, firstName: e.target.value })}
                className="input-field"
                required
              />
              <input
                type="text"
                placeholder="Child last name"
                value={child.lastName}
                onChange={(e) => setChild({ ...child, lastName: e.target.value })}
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Date of birth</label>
              <input
                type="date"
                value={child.dateOfBirth}
                onChange={(e) => setChild({ ...child, dateOfBirth: e.target.value })}
                max={new Date().toISOString().slice(0, 10)}
                className="input-field mt-1"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Primary sport</label>
              <select
                value={child.sport}
                onChange={(e) => setChild({ ...child, sport: e.target.value })}
                className="input-field mt-1"
                required
              >
                <option value="">Pick a sport</option>
                {SPORTS.map(s => (
                  <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 bg-gray-100 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {loading ? 'Creating...' : 'Create account'}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          Already have an account?{' '}
          <Link to="/login" className="text-yellow-600 hover:text-yellow-800 font-medium">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SignupParentPage;
